import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { ArrowRight, Check, ShieldCheck, Eye, Brain } from "lucide-react";

const PROBLEMI = [
  "Esperienze traumatiche e disturbo da stress post-traumatico",
  "Ansia e attacchi di panico",
  "Fobie",
  "Lutti e separazioni particolarmente difficili",
  "Esperienze di bullismo, umiliazione o rifiuto",
  "Esperienze relazionali dolorose",
  "Difficoltà legate all'autostima",
  "Conseguenze psicologiche di eventi improvvisi o fortemente stressanti",
  "Esperienze lavorative particolarmente stressanti",
];

const FASI = [
  {
    n: "01",
    t: "Individuazione dell'esperienza",
    d: "Si individua il ricordo, l'immagine o la situazione che continua ad avere un impatto emotivo nel presente, esplorando i pensieri negativi, le emozioni e le sensazioni corporee associate.",
  },
  {
    n: "02",
    t: "Stimolazione bilaterale",
    d: "Mantenendo l'attenzione sull'esperienza individuata, il terapeuta propone una stimolazione bilaterale alternata, che può avvenire attraverso movimenti oculari guidati, stimoli tattili o stimoli uditivi.",
  },
  {
    n: "03",
    t: "Elaborazione",
    d: "Tra una stimolazione e l'altra, la persona osserva ciò che emerge spontaneamente. Il terapeuta accompagna questo processo senza imporre ciò che la persona dovrebbe pensare o sentire.",
  },
];

const PREPARAZIONE = [
  {
    icon: ShieldCheck,
    t: "Condivisione e consenso",
    d: "La persona viene informata in modo chiaro e completo sull'EMDR, sugli obiettivi e su ciò che potrebbe sperimentare, potendo porre domande ed esprimere dubbi in modo consapevole.",
  },
  {
    icon: Brain,
    t: "Valutazione clinica",
    d: "Vengono considerate la storia personale, le esperienze traumatiche o stressanti, le risorse disponibili e la capacità di affrontare il materiale emotivo.",
  },
  {
    icon: Eye,
    t: "Preparazione e stabilizzazione",
    d: "Prima di lavorare direttamente sui ricordi traumatici, vengono consolidate le risorse della persona e insegnate strategie per riconoscere, tollerare e regolare le proprie emozioni.",
  },
];

const Kicker = ({ children, light }) => (
  <Reveal>
    <p className={`text-xs uppercase tracking-[0.28em] mb-6 ${light ? "text-sage-light" : "text-sage-dark"}`}>
      {children}
    </p>
  </Reveal>
);

export default function EmdrMacerata() {
  useSeo(
    "EMDR a Macerata: cos'è e come funziona | Susanna Paoletti",
    "EMDR a Macerata con la Dott.ssa Susanna Paoletti, psicoterapeuta formata al secondo livello: cos'è, come funziona una seduta e quando può essere utile.",
    "/emdr-macerata"
  );

  return (
    <>
      <PageHeader
        eyebrow="EMDR a Macerata"
        title="Cos'è l'EMDR e come funziona"
        subtitle="Se un'esperienza del passato continua a condizionare il tuo presente, l'EMDR può aiutarti a viverla in modo diverso. Ecco cos'è, come si svolge e quando può essere utile."
      />

      {/* Intro */}
      <section className="py-16 md:py-24" data-testid="emdr-intro">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-6">
          <Reveal>
            <p className="font-serif text-2xl md:text-3xl text-ink leading-snug tracking-tight text-balance">
              Se stai cercando informazioni sull'EMDR (dall'inglese <em>Eye Movement Desensitization and Reprocessing</em>,
              ossia Desensibilizzazione e Rielaborazione attraverso i Movimenti Oculari), è possibile che tu stia
              attraversando un momento difficile o che un'esperienza del passato continui, in qualche modo, a
              condizionare il tuo presente.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-lg text-stone leading-relaxed">
              In qualità di psicologa psicoterapeuta a Macerata, formata al secondo livello nell'utilizzo della
              tecnica EMDR, desidero spiegarti in modo semplice che cos'è questo approccio, come si svolge e in quali
              situazioni può essere utile.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Cos'è */}
      <section className="py-16 md:py-24 bg-sand" data-testid="emdr-cose">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Kicker>Il metodo</Kicker>
          <Reveal delay={0.04}>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-8">
              Cos'è l'EMDR?
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="space-y-5 text-lg text-stone leading-relaxed">
              <p>
                L'EMDR è un approccio psicoterapeutico strutturato, sostenuto da numerose evidenze scientifiche e
                raccomandato dalle principali linee guida internazionali per il trattamento del disturbo da stress
                post-traumatico (PTSD).
              </p>
              <p>
                Nel corso degli anni, il suo utilizzo si è esteso anche ad altre situazioni cliniche, in particolare
                quando esperienze difficili o traumatiche continuano a influenzare il modo in cui una persona vive il
                presente.
              </p>
              <p>
                L'EMDR non è una tecnica "magica" e non è ipnosi. È un percorso psicoterapeutico nel quale, attraverso
                un lavoro strutturato e rispettoso dei tempi della persona, si favorisce l'elaborazione di ricordi ed
                esperienze che possono essere rimasti associati a emozioni, pensieri e sensazioni particolarmente
                dolorose.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Perché */}
      <section className="py-16 md:py-24" data-testid="emdr-perche">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Kicker>Come funziona la mente</Kicker>
          <Reveal delay={0.04}>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-8">
              Perché un'esperienza può continuare a farci stare male?
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-lg text-stone leading-relaxed mb-8">
              Quando viviamo un'esperienza molto intensa o traumatica, può accadere che non venga elaborata
              completamente. Il ricordo può continuare a essere associato alle emozioni, alle sensazioni corporee e alle
              convinzioni negative su noi stessi che erano presenti nel momento dell'evento.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4">
            <Reveal delay={0.1}>
              <div className="h-full bg-sand border border-border rounded-2xl p-7">
                <h3 className="font-serif text-2xl text-ink leading-snug mb-3">La normale “digestione” psicologica</h3>
                <p className="text-stone leading-relaxed">
                  Possiamo immaginare il normale processo di elaborazione come una sorta di digestione: con il tempo,
                  un'esperienza difficile viene integrata nella nostra storia personale e diventa un ricordo del
                  passato, dal quale possiamo anche aver imparato qualcosa.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="h-full bg-sand border border-border rounded-2xl p-7">
                <h3 className="font-serif text-2xl text-ink leading-snug mb-3">Cosa accade nel blocco</h3>
                <p className="text-stone leading-relaxed">
                  Quando invece l'esperienza rimane scarsamente elaborata, può continuare a condizionare il presente.
                  Anche a distanza di anni, un odore, una parola, un luogo, una relazione o una situazione apparentemente
                  innocua possono riattivare emozioni molto intense: paura, vergogna, impotenza, rabbia o angoscia.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Prima di iniziare */}
      <section className="py-16 md:py-24 bg-ink text-cream relative overflow-hidden" data-testid="emdr-preparazione">
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <Kicker light>Sicurezza e rispetto dei tempi</Kicker>
          <Reveal delay={0.04}>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-6">
              Prima di iniziare il lavoro con l'EMDR
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-lg text-cream/75 leading-relaxed mb-10 max-w-3xl">
              L'utilizzo dell'EMDR non viene proposto automaticamente né applicato nello stesso modo a ogni persona.
              Prima del lavoro specifico di elaborazione, è fondamentale valutare insieme se questo approccio sia
              indicato e se vi siano le condizioni per affrontarlo in modo sicuro ed efficace. La preparazione è parte
              integrante del percorso.
            </p>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-3 gap-4">
            {PREPARAZIONE.map((p) => (
              <StaggerItem key={p.t}>
                <div className="h-full border border-cream/15 rounded-2xl p-7">
                  <p.icon size={24} className="text-sage-light mb-4" />
                  <h3 className="font-serif text-2xl leading-snug mb-3">{p.t}</h3>
                  <p className="text-cream/70 leading-relaxed text-sm">{p.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
        <div className="pointer-events-none absolute -bottom-32 -right-20 w-[380px] h-[380px] rounded-full bg-sage/20 blur-3xl" />
      </section>

      {/* Come funziona una seduta */}
      <section className="py-16 md:py-24" data-testid="emdr-seduta">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Kicker>La seduta</Kicker>
          <Reveal delay={0.04}>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-6">
              Come funziona una seduta di EMDR?
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-lg text-stone leading-relaxed mb-10 max-w-3xl">
              L'EMDR non richiede necessariamente di raccontare nei minimi dettagli l'evento doloroso. Il lavoro viene
              condotto rispettando i tempi, le risorse e le possibilità della persona, all'interno di una relazione
              terapeutica sicura. Durante la seduta, il terapeuta accompagna la persona attraverso diverse fasi:
            </p>
          </Reveal>
          <StaggerGroup className="space-y-4">
            {FASI.map((f) => (
              <StaggerItem key={f.n}>
                <div className="bg-sand border border-border rounded-2xl p-7 md:p-9 grid md:grid-cols-12 gap-4 md:gap-8">
                  <div className="md:col-span-2">
                    <span className="font-serif text-5xl text-sage">{f.n}</span>
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="font-serif text-2xl md:text-3xl text-ink leading-snug">{f.t}</h3>
                    <p className="text-stone leading-relaxed mt-3">{f.d}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal delay={0.1}>
            <p className="mt-10 font-serif italic text-2xl md:text-3xl text-sage-dark leading-snug max-w-3xl">
              L'obiettivo non è cancellare il ricordo: la storia rimane la stessa, ma può cambiare il modo in cui viene
              vissuta. Il ricordo perde progressivamente la sua intensità emotiva, diventando finalmente un'esperienza
              appartenente al passato.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Per quali problematiche */}
      <section className="py-16 md:py-24 bg-sand" data-testid="emdr-problemi">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Kicker>Ambiti di applicazione</Kicker>
          <Reveal delay={0.04}>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-6">
              Per quali problematiche può essere utile l'EMDR?
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-lg text-stone leading-relaxed mb-8 max-w-3xl">
              L'EMDR nasce come trattamento per il trauma e per il disturbo da stress post-traumatico, ma oggi viene
              utilizzato anche nel trattamento di diverse problematiche, tra cui:
            </p>
          </Reveal>
          <StaggerGroup className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {PROBLEMI.map((p, i) => (
              <StaggerItem key={i} y={12}>
                <div className="flex items-start gap-3">
                  <Check size={18} className="text-sage-dark mt-1 shrink-0" />
                  <span className="text-ink leading-snug">{p}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal delay={0.1}>
            <p className="text-stone leading-relaxed mt-8 max-w-3xl">
              Ogni percorso viene adattato alla persona, alla sua storia e agli obiettivi terapeutici, inserendosi
              all'interno di una psicoterapia globale.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Quando chiedere aiuto */}
      <section className="py-16 md:py-24" data-testid="emdr-quando">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Kicker>Un primo passo</Kicker>
          <Reveal delay={0.04}>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-8">
              Quando chiedere aiuto
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="space-y-5 text-lg text-stone leading-relaxed">
              <p>
                A volte non è necessario che sia accaduto qualcosa di "straordinario" perché un'esperienza possa
                continuare a pesare sul presente. Può essere sufficiente accorgersi che alcune emozioni, reazioni o
                modalità relazionali si ripetono, che determinate situazioni suscitano una risposta emotiva molto
                intensa o che qualcosa accaduto nel passato sembra ancora presente nella propria vita.
              </p>
              <p>
                In questi casi, iniziare un percorso psicoterapeutico può aiutare a comprendere ciò che sta accadendo e a
                trovare un modo diverso di stare con la propria storia. L'EMDR può essere uno degli strumenti attraverso
                cui affrontare questo percorso, quando indicato dalla valutazione clinica.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA finale */}
      <section className="pb-24 md:pb-36" data-testid="emdr-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-ink text-cream rounded-2xl p-10 md:p-16 text-center">
              <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight text-balance">
                Desideri valutare se questo percorso fa al caso tuo?
              </h2>
              <p className="mt-5 text-cream/75 leading-relaxed max-w-2xl mx-auto">
                Se desideri liberarti dal peso di un ricordo o di un'ansia che blocca la tua vita, puoi contattarmi per
                fissare un colloquio conoscitivo.
              </p>
              <div className="mt-10 flex justify-center">
                <PillButton to="/contatti" testid="emdr-contatti-cta">
                  Richiedi un primo colloquio <ArrowRight size={18} />
                </PillButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
